import {
  Box,
  Button,
  FormControl,
  Input,
  InputLabel,
  Select,
  styled,
  Tab,
  Tabs,
  Text,
} from '@ltvco/refresh-lib/theme';

export const SearchHubContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    boxShadow: '1px 1px 10px 10px rgba(0, 0, 0, 0.1)',
  },
}));

export const SearchButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2.75),
  width: '132px',
  height: '56px',
  borderRadius: '32px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '1.625em',
  letterSpacing: '0.02875em',
  textTransform: 'none',

  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const SearchFieldContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.text.disabled}`,
  borderRadius: '32px',
  padding: theme.spacing(0.375, 1),
  backgroundColor: theme.palette.common.white,
  width: '100%',
  margin: theme.spacing(4, 0),

  '&:focus-within': {
    borderColor: theme.palette.primary.main,
  },

  [theme.breakpoints.down('sm')]: {
    margin: 0,
  },
}));

export const StyledInputBase = styled(Input)(({ theme }) => ({
  width: '100%',
  height: '50px',
  position: 'relative',

  '&:before, &:after': {
    display: 'none',
  },

  '& .MuiInput-root, & .MuiInputBase-root, & .MuiOutlinedInput-root': {
    border: 'none',
    height: '50px',
    borderRadius: '32px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& .MuiInputBase-input': {
    padding: theme.spacing(1),
    fontSize: '16px',
    letterSpacing: '0.03125em',
    lineHeight: '1.5em',
    color: theme.palette.text.primary,

    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },

  '&.Mui-error': {
    '& .MuiInputBase-input': {
      border: `1px solid ${theme.palette.error.main}`,
    },
  },
}));

export const FormHelperErrorText = styled(Text)(({ theme }) => ({
  position: 'absolute',
  marginTop: theme.spacing(1.5),
  marginLeft: `-${theme.spacing(1)}`,
  fontSize: '12px',
  color: theme.palette.error.main,

  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1),
  },
}));

export const StyledTabsDesktop = styled(Tabs)(() => ({
  minHeight: 'unset',

  '& .MuiTabs-indicator': {
    display: 'none',
  },

  '& .MuiTabs-flexContainer': {
    flexWrap: 'wrap',
    rowGap: 8,
  },
}));

export const StyledTabDesktop = styled(Tab)(({ theme }) => ({
  borderRadius: '60px',
  padding: theme.spacing(0.5, 1.25),
  marginRight: theme.spacing(1),
  minHeight: 'unset',
  fontSize: '14px',
  fontWeight: 'bold',
  lineHeight: '1.2em',
  textTransform: 'none',
  backgroundColor: theme.palette.background.default,

  '& .MuiSvgIcon-root': {
    fill: theme.palette.text.contrastText,
    fontSize: '24px',
  },

  '& .MuiTab-iconWrapper': {
    padding: '4px',
    borderRadius: '50%',
    backgroundColor: theme.palette.text.secondary,
  },

  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,

    '& .MuiSvgIcon-root': {
      fill: theme.palette.primary.main,
    },

    '& .MuiTab-iconWrapper': {
      borderRadius: '50%',
      backgroundColor: theme.palette.text.contrastText,
    },

    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },

  '&:hover': {
    backgroundColor: theme.palette.background.light,
  },
}));

export const StyledTabsMobile = styled(Tabs)(({ theme }) => ({
  marginTop: theme.spacing(2.5),

  '& .MuiTabs-indicator': {
    display: 'none',
  },

  '& .MuiTabs-flexContainer': {
    flexWrap: 'wrap',
    width: '100%',
  },

  [theme.breakpoints.down('sm')]: {
    minHeight: 'unset',

    '& .MuiTabs-flexContainer': {
      gap: 0,
      rowGap: 8,
    },
  },
}));

export const StyledTabMobile = styled(Tab)(({ theme }) => ({
  borderRadius: '60px',
  gap: 8,
  padding: theme.spacing(0.75, 1.25),
  flexDirection: 'row',
  minHeight: 'unset',
  lineHeight: '1.2em',
  fontSize: '12px',
  fontWeight: 'bold',
  textTransform: 'none',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  whiteSpace: 'nowrap',

  '& .MuiSvgIcon-root': {
    fill: theme.palette.text.contrastText,
    fontSize: '24px',
  },

  '& .MuiTab-iconWrapper, & :where(.MuiSvgIcon-root)': {
    padding: '4px',
    borderRadius: '50%',
    backgroundColor: theme.palette.text.secondary,
    color: theme.palette.background.paper,
    marginBottom: 0,
  },

  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,

    '& .MuiSvgIcon-root': {
      fill: theme.palette.primary.main,
    },

    '& .MuiTab-iconWrapper, & :where(.MuiSvgIcon-root)': {
      backgroundColor: theme.palette.text.contrastText,
      borderRadius: '50%',
    },
  },
}));

export const HeaderText = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '34px',
  lineHeight: '1.412em',
  textAlign: 'center',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),

  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}));

export const StyledSubheaderText = styled(Text)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '14px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  position: 'absolute',
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  bottom: '0px',

  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    textAlign: 'left',
    margin: 0,
  },
}));

export const StyledSubheaderTextPhone = styled(Text)(({ theme }) => ({
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: 1.5,
  textAlign: 'left',
  color: theme.palette.text.secondary,
  position: 'absolute',
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
  bottom: '0px',
  width: '100%',
  boxSizing: 'border-box',

  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    margin: 0,
  },
}));

export const DisclaimerText = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,

  '& .MuiTypography-root': {
    lineHeight: '1em',
    fontSize: '12px',
  },
}));

export const FloatingLabel = styled(InputLabel)(({ theme }) => ({
  position: 'absolute',
  top: '-12px',
  left: '14px',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  padding: '0 4px',
  fontSize: '14px',
  zIndex: 1,
  opacity: 0,
  transform: 'translateY(10px)',
  pointerEvents: 'none',
  transition:
    'transform 0.2s ease-out, opacity 0.2s ease-out, color 0.2s ease-out',

  '&.error': {
    color: theme.palette.error.main,
  },

  '&.focused': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
}));

export const StyledSearchTypeSelect = styled(Select)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.default,

  '&:hover': {
    boxShadow: 'none',
  },

  '& .MuiSelect-icon': {
    color: theme.palette.text.primary,
    right: theme.spacing(1),
  },

  '&.MuiInputBase-root': {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      boxShadow: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      boxShadow: 'none',
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    padding: 0,
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    fontWeight: 500,
    backgroundColor: theme.palette.background.paper,

    '& .MuiSelect-select': {
      width: '100%',
    },
  },
}));

export const StyledSearchTypeSelectContainer = styled(FormControl)(
  ({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .MuiOutlinedInput-root': {
      height: '100%',
      borderRadius: '32px 0 0 32px',
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',

      '& .MuiOutlinedInput-root': {
        height: '100%',
        borderRadius: '32px',
      },
    },
  })
);
